import { useSectionAccess } from "../auth/useSectionAccess";
import { useEffect, useMemo, useState, type CSSProperties } from "react";
import { formatDateShort, formatMoney } from "../lib/format";
import { PanelLoading } from "../components/Loading";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { leadsApi, type Lead, type LeadStage } from "../api/leads";
import type { ApiError } from "../api/client";
import { useToast } from "../components/Toast";
import { usePrompt } from "../components/ConfirmDialog";
import Ic from "../components/icons.jsx";
import { LeadClassificationChip } from "../components/LeadClassificationChip";
import { LeadsFilterBar } from "../components/leads/LeadsFilterBar";
import { LeadsViewSwitcher } from "../components/leads/LeadsViewSwitcher";
import { ownerInitials } from "./offersBoard";
import { LoadFailed } from "../components/LoadFailed";
import { stageColor, tint, kindOf } from "./leadPipeline";
import { groupLeadsByStage, type LeadBoardColumn } from "./leadsBoard";
import {
  filterLeads, hasActiveLeadFilter, distinctOwners, distinctCountries,
  EMPTY_LEAD_FILTER, type LeadFilterCriteria,
} from "./leadsFilter";

const money = (n: number | null | undefined, cur: string | null | undefined) =>
  formatMoney(n, cur);

/**
 * Lidovi Kanban board — default /leads route, mirroring OffersBoardPage.
 * Columns are the fixed 9 pipeline phases (leadPipeline.LEAD_STAGES order);
 * cards are HTML5-draggable between any two columns (free movement, same as
 * the detail page's stepper). Dropping into a LOST-kind phase prompts for the
 * mandatory loss reason — cancelling the prompt
 * cancels the move. Filters mirror LeadsPage via the same leadsFilter.ts
 * criteria; grouping/sort/sum logic lives in leadsBoard.ts.
 */
export default function LeadsBoardPage() {
  const { canWrite } = useSectionAccess("leads");
  const { t } = useTranslation();
  const toast = useToast();
  const prompt = usePrompt();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = useState<Lead[] | null>(null);
  // The create modal lives on LeadsPage (the list) — the board's "+ Nov lid"
  // routes there with ?new=1 which auto-opens it, instead of duplicating a
  // 160-line form here.
  const openCreate = () => { if (canWrite) navigate("/leads/list?new=1"); };
  const [criteria, setCriteria] = useState<LeadFilterCriteria>(() => ({
    ...EMPTY_LEAD_FILTER,
    search: searchParams.get("q") ?? "",
  }));
  const [dragging, setDragging] = useState<Lead | null>(null);
  /** Poruka kvara; prazna tabla i neuspelo učitavanje ne smeju da izgledaju isto. */
  const [greska, setGreska] = useState<string | null>(null);
  const [osvezi, setOsvezi] = useState(0);

  useEffect(() => {
    leadsApi.list()
      .then((v) => { setItems(v); setGreska(null); })
      // Prazan spisak i neuspelo učitavanje nisu ista stvar; vidi `LoadFailed`.
      .catch((e: ApiError) => { toast.error(e.message); setGreska(e.message); });
  }, [toast, osvezi]);

  // Same "q"-only URL-sync idiom as the offers board — see LeadsViewSwitcher.
  const setSearch = (v: string) => {
    setCriteria((c) => ({ ...c, search: v }));
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (v) next.set("q", v); else next.delete("q");
      return next;
    }, { replace: true });
  };

  const owners = useMemo(() => distinctOwners(items ?? []), [items]);
  const countries = useMemo(() => distinctCountries(items ?? []), [items]);
  const showOwnerFilter = owners.length > 1;
  const filtered = useMemo(() => filterLeads(items ?? [], criteria), [items, criteria]);
  const filtersActive = hasActiveLeadFilter(criteria);
  const columns = useMemo(() => groupLeadsByStage(filtered), [filtered]);

  /**
   * Optimistic drag&drop move (OffersBoardPage.moveOfferTo idiom). A LOST
   * target first collects the mandatory loss reason; the card only jumps
   * once the reason is confirmed, and rolls back if the API refuses.
   */
  async function moveLeadTo(lead: Lead, toStage: LeadStage) {
    setDragging(null);
    if (!canWrite || lead.stage === toStage) return;
    let lossReason: string | undefined;
    if (kindOf(toStage) === "LOST") {
      const reason = await prompt({
        title: t("leads.lossReason.title", "Lost lead"),
        message: t("leads.lossReason.message", "Why is {{name}} moving to “{{stage}}”? The reason is kept on the lead.", { name: lead.name, stage: t(`leads.stage.${toStage}`, toStage) }),
        placeholder: t("leads.lossReason.placeholder", "Reason for the loss…"),
        multiline: true,
        required: true,
        danger: true,
        confirmLabel: t("leads.lossReason.confirm", "Move"),
      });
      if (reason == null || !reason.trim()) return;
      lossReason = reason.trim();
    }
    const prev = lead.stage;
    setItems((p) => p?.map((l) => (l.id === lead.id ? { ...l, stage: toStage } : l)) ?? null);
    try {
      const updated = await leadsApi.changeStage(lead.id, toStage, lossReason);
      setItems((p) => p?.map((l) => (l.id === lead.id ? updated : l)) ?? null);
      toast.success(t("leads.toast.stageChanged", { stage: t(`leads.stage.${toStage}`), defaultValue: "Moved to {{stage}}" }));
    } catch (err) {
      toast.error((err as ApiError).message);
      setItems((p) => p?.map((l) => (l.id === lead.id ? { ...l, stage: prev } : l)) ?? null);
    }
  }

  return (
    <div className="fadein" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <header style={pageHead}>
        <div>
          <h1 style={{ margin: 0, fontSize: 24, color: "var(--ink)" }}>{t("leads.list.title", "Leads")}</h1>
          <div style={subText}>
            {items === null
              ? t("common.loading")
              : t("leads.list.summary", { count: items.length, defaultValue: "{{count}} leads" })}
          </div>
        </div>
        <button type="button" className="btn primary" disabled={!canWrite} onClick={openCreate}>
          <Ic.plus /> {t("leads.list.new", "New lead")}
        </button>
      </header>

      <LeadsViewSwitcher active="board" search={criteria.search} />

      {items != null && items.length > 0 && (
        <LeadsFilterBar
          criteria={criteria}
          onSearchChange={setSearch}
          onStageChange={(v) => setCriteria((c) => ({ ...c, stage: v }))}
          onOwnerChange={(v) => setCriteria((c) => ({ ...c, ownerId: v }))}
          onCountryChange={(v) => setCriteria((c) => ({ ...c, country: v }))}
          onClassificationChange={(v) => setCriteria((c) => ({ ...c, classification: v }))}
          owners={owners}
          countries={countries}
          showOwnerFilter={showOwnerFilter}
          shownCount={filtered.length}
          totalCount={items.length}
          filtersActive={filtersActive}
          onClear={() => setCriteria((c) => ({ ...EMPTY_LEAD_FILTER, search: c.search }))}
        />
      )}

      {greska ? (
        <LoadFailed message={greska} onRetry={() => { setGreska(null); setItems(null); setOsvezi((n) => n + 1); }} />
      ) : items === null ? (
        <PanelLoading text={t("common.loading")} />
      ) : items.length === 0 ? (
        <div style={emptyCard}>
          <div style={{ fontSize: 28, marginBottom: 4 }}>🌱</div>
          <b style={{ color: "var(--ink-2)" }}>{t("leads.empty.title", "No leads yet.")}</b>
          <div style={{ color: "var(--ink-3)", fontSize: 13, marginTop: 4 }}>
            {t("leads.empty.hint", "Add your first potential customer.")}
          </div>
          <button type="button" className="btn primary" disabled={!canWrite} onClick={openCreate} style={{ marginTop: 12 }}>
            <Ic.plus /> {t("leads.list.new", "New lead")}
          </button>
        </div>
      ) : (
        <div style={boardScroll}>
          {columns.map((col) => (
            <BoardColumnView
              key={col.stage}
              column={col}
              dragging={dragging}
              onDragStart={setDragging}
              onDragEnd={() => setDragging(null)}
              onDrop={(l) => void moveLeadTo(l, col.stage)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function BoardColumnView({
  column, dragging, onDragStart, onDragEnd, onDrop,
}: {
  column: LeadBoardColumn<Lead>;
  dragging: Lead | null;
  onDragStart: (l: Lead) => void;
  onDragEnd: () => void;
  onDrop: (l: Lead) => void;
}) {
  const { t } = useTranslation();
  const [over, setOver] = useState(false);
  const color = stageColor(column.stage);
  const canDrop = useSectionAccess("leads").canWrite && !!dragging && dragging.stage !== column.stage;
  const sum = money(column.sumValue, "EUR");

  return (
    <div
      role="group"
      aria-label={t("leads.board.columnAria", { name: t(`leads.stage.${column.stage}`, column.stage), count: column.leads.length, sum, defaultValue: "{{name}}: {{count}} leads, {{sum}}" })}
      style={{
        ...boardCol,
        ...(canDrop && over ? { borderColor: color, background: tint(color) } : {}),
      }}
      onDragOver={(e) => { if (!canDrop) return; e.preventDefault(); e.dataTransfer.dropEffect = "move"; if (!over) setOver(true); }}
      onDragLeave={(e) => { if (e.currentTarget.contains(e.relatedTarget as Node)) return; setOver(false); }}
      onDrop={(e) => { e.preventDefault(); setOver(false); if (canDrop && dragging) onDrop(dragging); }}
    >
      <header style={colHead}>
        <div style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0 }}>
          <span aria-hidden style={{ width: 8, height: 8, borderRadius: "50%", background: color, flex: "none" }} />
          <strong style={colTitle} title={t(`leads.stage.${column.stage}`, column.stage)}>{t(`leads.stage.${column.stage}`, column.stage)}</strong>
          <span style={{ color: "var(--ink-4)", fontSize: 12, flex: "none" }}>{column.leads.length}</span>
        </div>
        <span style={colSum}>{sum}</span>
      </header>
      <div style={colList}>
        {column.leads.length === 0 ? (
          <div style={emptyCol}>—</div>
        ) : (
          column.leads.map((lead) => (
            <LeadCard
              key={lead.id}
              lead={lead}
              dragging={dragging?.id === lead.id}
              onDragStart={() => onDragStart(lead)}
              onDragEnd={onDragEnd}
            />
          ))
        )}
      </div>
    </div>
  );
}

function LeadCard({
  lead, dragging, onDragStart, onDragEnd,
}: {
  lead: Lead;
  dragging?: boolean;
  onDragStart?: () => void;
  onDragEnd?: () => void;
}) {
  const { canWrite } = useSectionAccess("leads");
  const { t } = useTranslation();
  const subtitle = [lead.contactPerson, lead.contactPosition].filter(Boolean).join(" · ");
  return (
    <Link to={`/leads/${lead.id}`} style={{ textDecoration: "none", color: "inherit" }}>
      <article
        draggable={canWrite}
        onDragStart={(e) => {
          e.dataTransfer.setData("text/plain", lead.id);
          e.dataTransfer.effectAllowed = "move";
          onDragStart?.();
        }}
        onDragEnd={onDragEnd}
        style={{ ...leadCard, ...(dragging ? { opacity: 0.5, transform: "scale(0.98)" } : {}) }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0 }}>
          <span style={{ fontWeight: 600, color: "var(--ink)", fontSize: 13, lineHeight: 1.3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{lead.name}</span>
          {lead.convertedCompanyId && <span style={convertedChip}>{t("leads.badge.converted", "Converted")}</span>}
        </div>
        {subtitle && (
          <div style={{ fontSize: 11.5, color: "var(--ink-3)", marginTop: 3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {subtitle}
          </div>
        )}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 6, gap: 6 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: "var(--ink-2)", fontVariantNumeric: "tabular-nums" }}>
            {money(lead.value, lead.currency)}
          </span>
          {lead.classification && <LeadClassificationChip classification={lead.classification} />}
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 8, gap: 6 }}>
          <span title={lead.ownerName ?? undefined} aria-hidden style={ownerAvatar}>
            {ownerInitials(lead.ownerName)}
          </span>
          <span style={{ fontSize: 10, color: "var(--ink-4)" }}>
            {formatDateShort(lead.updatedAt)}
          </span>
        </div>
      </article>
    </Link>
  );
}

const pageHead: CSSProperties = { display: "flex", justifyContent: "space-between", alignItems: "flex-end", gap: 12, flexWrap: "wrap" };
const subText: CSSProperties = { fontSize: 13, color: "var(--ink-3)", marginTop: 4 };
const emptyCard: CSSProperties = { background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 10, padding: 32, textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center" };
const convertedChip: CSSProperties = { fontSize: 10, fontWeight: 700, color: "var(--ok)", background: "var(--ok-bg)", padding: "1px 6px", borderRadius: 999, flex: "none" };
const boardScroll: CSSProperties = { display: "flex", gap: 12, overflowX: "auto", paddingBottom: 8, alignItems: "flex-start" };
const boardCol: CSSProperties = {
  background: "var(--surface-2)", border: "1px solid var(--border)", borderRadius: 10, padding: 10,
  minHeight: 200, display: "flex", flexDirection: "column", gap: 8, flex: "1 1 200px", minWidth: 200, maxWidth: 300,
  transition: "border-color .12s ease, background .12s ease",
};
const colHead: CSSProperties = {
  display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8,
  paddingBottom: 6, borderBottom: "1px solid var(--border)",
};
const colTitle: CSSProperties = { fontSize: 13, color: "var(--ink)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" };
const colSum: CSSProperties = { fontSize: 12, fontWeight: 600, color: "var(--ink-3)", fontVariantNumeric: "tabular-nums", flex: "none", whiteSpace: "nowrap" };
const colList: CSSProperties = { display: "flex", flexDirection: "column", gap: 8, flex: 1, minHeight: 40 };
const emptyCol: CSSProperties = { color: "var(--ink-4)", fontSize: 12, textAlign: "center", padding: "16px 0" };
const leadCard: CSSProperties = {
  background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 8, padding: 10,
  display: "flex", flexDirection: "column", boxShadow: "var(--shadow-1)",
  cursor: "grab", transition: "transform .12s ease, box-shadow .12s ease",
};
const ownerAvatar: CSSProperties = {
  display: "inline-flex", alignItems: "center", justifyContent: "center",
  width: 22, height: 22, borderRadius: "50%", background: "var(--brand-primary)",
  color: "var(--on-primary)", fontSize: 9.5, fontWeight: 700, flex: "none",
};
